import { z } from 'zod';
// The Do Not Call list: numbers no agent calls out to. Adding a number only ever stops calls; removing one, or turning
// tracking off, lets calls reach people who asked not to be called, so both always ask first.
/** How many numbers list_do_not_call_numbers returns at most. */
export const DO_NOT_CALL_NUMBERS_SHOWN = 50;
export const listDoNotCallNumbersToolSchema = {
    name: 'list_do_not_call_numbers',
    description: 'Check the Do Not Call list: whether tracking is on, how many numbers it holds, and the latest numbers (or those matching a search).',
    inputSchema: z.object({
        search: z
            .string()
            .optional()
            .describe('Digits the number contains, such as an area code or a whole number')
    }),
    outputSchema: z.object({
        trackingEnabled: z.boolean().describe('Agents add callers who ask not to be called'),
        total: z.number(),
        matching: z.number(),
        numbers: z.array(z.object({
            phoneNumber: z.string(),
            added: z.string(),
            conversationId: z
                .string()
                .optional()
                .describe('The call where they asked not to be called; missing if added by hand')
        }))
    })
};
export const addDoNotCallNumbersToolSchema = {
    name: 'add_do_not_call_numbers',
    description: 'Add phone numbers to the Do Not Call list, so no agent calls them. US numbers can leave out the +1.',
    inputSchema: z.object({ phoneNumbers: z.array(z.string().min(7)).min(1).max(100) }),
    outputSchema: z.object({ added: z.number(), alreadyListed: z.number() })
};
export const removeDoNotCallNumberToolSchema = {
    name: 'remove_do_not_call_number',
    description: 'Take a phone number off the Do Not Call list, so agents can call it again.',
    inputSchema: z.object({ phoneNumber: z.string().describe('As list_do_not_call_numbers shows it') }),
    outputSchema: z.object({ phoneNumber: z.string() })
};
export const setDoNotCallTrackingToolSchema = {
    name: 'set_do_not_call_tracking',
    description: 'Turn Do Not Call tracking on or off. When on, agents add callers who ask not to be called to the list during the call.',
    inputSchema: z.object({ enabled: z.boolean() }),
    outputSchema: z.object({ trackingEnabled: z.boolean() })
};
const digitsOf = (text) => text.replace(/\D/g, '');
/** The same number, however it's written: +1 (555) 010-0199 matches 5550100199. */
export const isSameNumber = (listed, asked) => {
    const [listedDigits, askedDigits] = [digitsOf(listed), digitsOf(asked)];
    return listedDigits === askedDigits || listedDigits === `1${askedDigits}`;
};
export const describeDoNotCallList = (entries, { trackingEnabled, search }) => {
    const searchDigits = search ? digitsOf(search) : '';
    const matches = entries.filter((entry) => digitsOf(entry.customerPhoneNumber).includes(searchDigits));
    return {
        trackingEnabled,
        total: entries.length,
        matching: matches.length,
        numbers: matches.slice(0, DO_NOT_CALL_NUMBERS_SHOWN).map((entry) => ({
            phoneNumber: entry.customerPhoneNumber,
            added: new Date(entry.createdAt).toISOString().slice(0, 10),
            ...(entry.vapiCallId && { conversationId: entry.vapiCallId })
        }))
    };
};
//# sourceMappingURL=do-not-call.js.map